import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  useEffect(() => {
    if (!user || !token) {
      navigate('/');
    }
  }, [user, token]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-8">My Account</h1>

        <div className="bg-white rounded-2xl border border-stone-100 p-6">
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-stone-100">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-xl text-stone-900">{user.name}</h2>
              <p className="text-muted-foreground">{user.email}</p>
              {user.role === 'admin' && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                  Admin
                </span>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" /> Full Name
              </Label>
              <Input value={user.name} disabled className="bg-muted/50" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" /> Email Address
              </Label>
              <Input value={user.email} disabled className="bg-muted/50" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" /> Phone Number
              </Label>
              <Input value={user.phone || 'Not provided'} disabled className="bg-muted/50" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" /> Account Type
              </Label>
              <Input value={user.role === 'admin' ? 'Administrator' : 'Customer'} disabled className="bg-muted/50" />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-stone-100">
            <Button
              variant="outline"
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="w-full text-destructive border-destructive/50 hover:bg-destructive/10 rounded-full"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
